/**
 * Catalog API Service
 * Material catalog browsing, search, and comparison
 */

import { apiClient } from "@/lib/api-client";
import {
	brandListSchema,
	categoryListSchema,
	categoryTreeSchema,
	compareSchema,
	productDetailPayloadSchema,
	productSearchResultSchema,
	variantListSchema,
} from "./catalog-api.schemas";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

/*
 * The catalogue shapes now live in `@shared/types`, because `apps/admin` needs
 * the same ones and a second copy is a second thing to drift. They are
 * re-exported here so the many existing importers of this module keep working
 * and the move stays a one-file change.
 *
 * One shape changed rather than moved: `specifications` now admits `boolean`,
 * which the database, the Zod schema and the service have always accepted. The
 * frontend type did not, so a product with "Anti-skid: true" carried a value the
 * compiler insisted could not exist.
 */
import type {
	CatalogBrand,
	CatalogCategory,
	CatalogCategoryTreeNode,
	CatalogProduct,
	ProductVariant,
} from "@shared/types";
import { parseResponseData, ResponseShapeError } from "@shared/types";

export type {
	CatalogBrand,
	CatalogCategory,
	CatalogCategoryTreeNode,
	CatalogProduct,
	ProductPrice,
	ProductSpecifications,
	ProductStatus,
	ProductTier,
	ProductVariant,
	PublishBlocker,
	SpecificationValue,
	SpecTemplate,
	SpecTemplateAttribute,
	SpecTemplateVariantAttribute,
} from "@shared/types";

/**
 * Every facet block `GET /catalog/products` returns, mirroring the backend's
 * `PRODUCT_FACET_FIELDS`. Plan 003 promoted eight more attributes out of the
 * `specifications` blob into faceted columns, so the response went from three
 * blocks to eleven while this type still declared three -- the extra blocks
 * arrived and were silently unreachable.
 *
 * Note the plural names: `roomTypes`, `applicationAreas` and `styleTags` come
 * back plural but are *requested* singular (`roomType`, `applicationArea`,
 * `styleTag`), because the filter names one value and the document holds many.
 */
export const CATALOG_FACET_FIELDS = [
	"categoryName",
	"brandName",
	"tier",
	"materialFamily",
	"designFamily",
	"finish",
	"colourFamily",
	"sizeLabel",
	"roomTypes",
	"applicationAreas",
	"styleTags",
] as const;

export type CatalogFacetField = (typeof CATALOG_FACET_FIELDS)[number];

export interface CatalogFacetCount {
	value: string;
	count: number;
}

export interface ProductSearchResult {
	products: {
		id: string;
		name: string;
		description?: string;
		brandName: string;
		brandId: string;
		categoryName: string;
		categoryId: string;
		categorySlug: string;
		tier: string;
		uom: string;
		/** Paise, GST included; null when the product has no customer price. */
		minPrice: number | null;
		maxPrice: number | null;
		hasVariants: boolean;
		variantCount: number;
		primaryImage?: string;
	}[];
	total: number;
	page: number;
	totalPages: number;
	// Partial because a facet with no matching documents is omitted entirely
	// rather than returned empty.
	facets?: Partial<Record<CatalogFacetField, CatalogFacetCount[]>>;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * A 2xx that did not parse is a client/server contract bug, not a failed
 * request, and the callers below turn every failure into an empty value. Logging
 * it is what keeps that from reading as "no results".
 */
function reportShapeError(error: unknown): void {
	if (error instanceof ResponseShapeError) console.error(error);
}

/**
 * The flat list of every active category, in `displayOrder` then name order.
 *
 * Since plan 003 that is 674 rows spanning three levels with no ordering
 * between them, so it is no longer something to put in front of a user. Use
 * `getCategoryTree` for anything a person picks from; this stays for callers
 * that want a lookup table.
 */
export async function getCategories(): Promise<CatalogCategory[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/categories`);
		return parseResponseData(
			response.data,
			categoryListSchema,
			"GET /api/v1/catalog/categories",
		).categories;
	} catch (error) {
		console.error("Failed to fetch categories:", error);
		return [];
	}
}

/**
 * The same categories nested into the L1 > L2 > L3 tree, roots first.
 *
 * `tree=true` is opt-in on the endpoint and the flat response is still the
 * default, so this has to be asked for explicitly.
 *
 * Returns `null` -- not `[]` -- when the request fails. The two are opposite
 * claims about a `categoryId` the tree does not contain: after a successful
 * load it means the category is gone, after a failed one it means nothing was
 * checked. Collapsing both to `[]` is how a dropped connection came to be
 * reported to the user as a deleted category.
 */
export async function getCategoryTree(): Promise<
	CatalogCategoryTreeNode[] | null
> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/categories`, {
			params: { tree: "true" },
		});
		return parseResponseData(
			response.data,
			categoryTreeSchema,
			"GET /api/v1/catalog/categories?tree=true",
		).categories;
	} catch (error) {
		console.error("Failed to fetch category tree:", error);
		return null;
	}
}

export async function getBrands(): Promise<CatalogBrand[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/brands`);
		return parseResponseData(
			response.data,
			brandListSchema,
			"GET /api/v1/catalog/brands",
		).brands;
	} catch (error) {
		console.error("Failed to fetch brands:", error);
		return [];
	}
}

export async function searchCatalogProducts(params?: {
	q?: string;
	categoryId?: string;
	/** One brand id, or several to OR together. */
	brandId?: string | string[];
	/** One tier, or several to OR together. */
	tier?: string | string[];
	/**
	 * One `item_kind`, or several to OR together.
	 *
	 * Defaults to `material` alone when omitted. When set to `all`, customer
	 * browse searches across customer-visible types (material, labour, service).
	 */
	itemKind?: string | string[];
	priceMin?: number;
	priceMax?: number;
	page?: number;
	perPage?: number;
	sortBy?: string;
}): Promise<ProductSearchResult> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/products`, {
			params,
			// Send arrays as `brandId=a&brandId=b` rather than axios's default
			// `brandId[]=a&brandId[]=b`, which Express would surface under the key
			// "brandId[]" and the handler would never see. Applies to every
			// repeatable param here -- `brandId`, `tier`, `itemKind`.
			paramsSerializer: { indexes: null },
		});
		return parseResponseData(
			response.data,
			productSearchResultSchema,
			"GET /api/v1/catalog/products",
		);
	} catch (error) {
		console.error("Failed to search catalog products:", error);
		return { products: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function getCatalogProduct(
	id: string,
): Promise<CatalogProduct | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/products/${id}`);
		const payload = parseResponseData(
			response.data,
			productDetailPayloadSchema,
			"GET /api/v1/catalog/products/:id",
		);
		// `price`, `prices`, `sourceable` and `unsourceableReason` are all siblings
		// of `product` in the response, not fields on it. Unwrapping `.product`
		// alone silently drops them -- which is how the price pair was lost once
		// already, leaving the page on the legacy `mrp` mirror, and how the
		// sourceability pair was lost again the moment the backend added it.
		//
		// `sourceable` is what lets the page say "no longer available" instead of
		// showing a withdrawn product as buyable; dropping it here made the
		// backend's whole report-rather-than-gate design unobservable.
		//
		// `specTemplate` is the THIRD, and it was still missing while the comment
		// above described the same loss twice. The product page reads each spec
		// row's unit and displayOrder from it, so without it
		// `product.specTemplate` is permanently undefined, the ordering branch in
		// `orderedSpecs` never runs, and every specification renders unitless in
		// whatever order the JSON object happens to hold. Not a fallback: dead
		// code behind a live-looking `if`. It also made an admin dialog's warning
		// false, since removing a template cannot cost a page something it was
		// never given.
		//
		// It no longer needs UNWRAPPING here: the endpoint used to send
		// `{ template, attributes }` while `CatalogProduct` typed the field flat,
		// so this hand-adapted it and the admin app redeclared the nested shape --
		// two private descriptions of one contract. The server flattens at the
		// boundary now, so this passes it straight through like the others.
		//
		// `brand`, `category` and `variants` are siblings too, and were the
		// fourth loss: the product page's brand link, its category crumb and its
		// variant picker all read them, so none of the three rendered (#506).
		return {
			...payload.product,
			brand: payload.brand ?? undefined,
			category: payload.category ?? undefined,
			// Inactive variants are not offered: the backend returns them (it
			// filters only deleted ones), and the BOQ's Add Entry does not offer
			// one, so a shortlisted inactive variant could never reach a BOQ.
			variants: (payload.variants ?? []).filter(
				(variant) => variant.isActive !== false,
			),
			price: payload.price ?? null,
			prices: payload.prices ?? [],
			sourceable: payload.sourceable ?? true,
			unsourceableReason: payload.unsourceableReason ?? null,
			specTemplate: payload.specTemplate ?? undefined,
		};
	} catch (error) {
		reportShapeError(error);
		return null;
	}
}

export async function getProductVariants(
	id: string,
): Promise<ProductVariant[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/catalog/products/${id}/variants`,
		);
		return parseResponseData(
			response.data,
			variantListSchema,
			"GET /api/v1/catalog/products/:id/variants",
		).variants;
	} catch (error) {
		reportShapeError(error);
		return [];
	}
}

export async function compareProducts(
	ids: string[],
): Promise<CatalogProduct[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/catalog/compare`, {
			params: { ids: ids.join(",") },
		});
		const items = parseResponseData(
			response.data,
			compareSchema,
			"GET /api/v1/catalog/compare",
		).products;
		return items.map((item) => ({
			...item.product,
			brand: item.brand,
			category: item.category,
			variants: item.variants,
			// Siblings of `product`, like in getCatalogProduct -- they come from the
			// projection, not the products row.
			price: item.price ?? null,
			prices: item.prices ?? [],
		}));
	} catch (error) {
		reportShapeError(error);
		return [];
	}
}

// ============================================================================
// PHASE 2 — DESIGNER CATALOG (SLICE B)
// ============================================================================

export interface DesignerCatalogFavorite {
	id: string;
	catalogProductId: string;
	createdAt: string;
	product: {
		id: string;
		name: string;
		description: string | null;
		images: string[];
	};
}

export interface DesignerCatalogCollection {
	id: string;
	designerId: string;
	name: string;
	description: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface DesignerCatalogCollectionDetail {
	collection: DesignerCatalogCollection;
	items: Array<{
		id: string;
		catalogProductId: string;
		createdAt: string;
		product: { id: string; name: string; images: string[] };
	}>;
}

export interface DesignerCustomProduct {
	id: string;
	designerId: string;
	name: string;
	description: string | null;
	brandName: string | null;
	categoryId: string | null;
	price: number | null;
	imageUrls: string[];
	specs: Record<string, string | number | boolean>;
	createdAt: string;
	updatedAt: string;
}

// Favorites

export async function listFavorites(): Promise<DesignerCatalogFavorite[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/designer-catalog/favorites`,
		);
		return response.data.data ?? [];
	} catch {
		return [];
	}
}

export async function addFavorite(
	productId: string,
): Promise<{ success: boolean; id?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/designer-catalog/favorites/${productId}`,
		);
		return { success: true, id: response.data.data.id };
	} catch {
		return { success: false };
	}
}

export async function removeFavorite(
	productId: string,
): Promise<{ success: boolean }> {
	try {
		await apiClient.delete(
			`${API_BASE}/designer-catalog/favorites/${productId}`,
		);
		return { success: true };
	} catch {
		return { success: false };
	}
}

// Collections

export async function listCollections(): Promise<DesignerCatalogCollection[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/designer-catalog/collections`,
		);
		return response.data.data ?? [];
	} catch {
		return [];
	}
}

export async function getCollection(
	id: string,
): Promise<DesignerCatalogCollectionDetail | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/designer-catalog/collections/${id}`,
		);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function createCollection(payload: {
	name: string;
	description?: string;
}): Promise<{
	success: boolean;
	collection?: DesignerCatalogCollection;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/designer-catalog/collections`,
			payload,
		);
		return { success: true, collection: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create collection",
		};
	}
}

export async function updateCollection(
	id: string,
	payload: { name?: string; description?: string },
): Promise<{
	success: boolean;
	collection?: DesignerCatalogCollection;
	error?: string;
}> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/designer-catalog/collections/${id}`,
			payload,
		);
		return { success: true, collection: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update collection",
		};
	}
}

export async function deleteCollection(
	id: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/designer-catalog/collections/${id}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete collection",
		};
	}
}

export async function addCollectionItem(
	collectionId: string,
	catalogProductId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/designer-catalog/collections/${collectionId}/items`,
			{
				catalogProductId,
			},
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add item to collection",
		};
	}
}

export async function removeCollectionItem(
	collectionId: string,
	catalogProductId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(
			`${API_BASE}/designer-catalog/collections/${collectionId}/items/${catalogProductId}`,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to remove item from collection",
		};
	}
}

// Custom Products

export async function listCustomProducts(): Promise<DesignerCustomProduct[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/designer-catalog/custom-products`,
		);
		return response.data.data ?? [];
	} catch {
		return [];
	}
}

export async function getCustomProduct(
	id: string,
): Promise<DesignerCustomProduct | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/designer-catalog/custom-products/${id}`,
		);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function createCustomProduct(payload: {
	name: string;
	description?: string;
	brandName?: string;
	categoryId?: string;
	price?: number;
	imageUrls?: string[];
	specs?: Record<string, string | number | boolean>;
}): Promise<{
	success: boolean;
	product?: DesignerCustomProduct;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/designer-catalog/custom-products`,
			payload,
		);
		return { success: true, product: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create custom product",
		};
	}
}

export async function updateCustomProduct(
	id: string,
	payload: {
		name?: string;
		description?: string;
		brandName?: string;
		categoryId?: string;
		price?: number;
		imageUrls?: string[];
		specs?: Record<string, string | number | boolean>;
	},
): Promise<{
	success: boolean;
	product?: DesignerCustomProduct;
	error?: string;
}> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/designer-catalog/custom-products/${id}`,
			payload,
		);
		return { success: true, product: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update custom product",
		};
	}
}

export async function deleteCustomProduct(
	id: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(
			`${API_BASE}/designer-catalog/custom-products/${id}`,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete custom product",
		};
	}
}
